import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { 
  Users, UserPlus, Mail, Trash2, Clock, CheckCircle, 
  XCircle, Copy, Shield, User, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const UserManagement = () => {
  const [data, setData] = useState({
    users: [],
    pending_invites: [],
    total_users: 0,
    max_users: 5,
    slots_available: 5
  });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    nome: '',
    role: 'gestor'
  });
  const [inviteLink, setInviteLink] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Apenas administradores podem acessar esta página');
      } else {
        toast.error('Erro ao carregar usuários');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/users/invite`, inviteForm);
      toast.success(response.data.message);
      setInviteLink(response.data.invite_url);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar convite');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Tem certeza que deseja cancelar este convite?')) return;
    try {
      await axios.delete(`${API_URL}/users/invite/${inviteId}`);
      toast.success('Convite cancelado');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao cancelar convite');
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja remover ${userName}?`)) return;
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      toast.success('Usuário removido');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover usuário');
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      toast.success('Link copiado!');
    }
  };

  const resetInviteForm = () => {
    setInviteForm({ email: '', nome: '', role: 'gestor' });
    setInviteLink(null);
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge className="bg-purple-500"><Shield size={12} className="mr-1" />Admin</Badge>;
    }
    return <Badge className="bg-blue-500"><User size={12} className="mr-1" />Gestor</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Gerenciar Usuários
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Convide colaboradores para acessar o sistema
          </p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) resetInviteForm(); }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-emerald-500 hover:bg-emerald-600" 
              disabled={data.slots_available <= 0}
              data-testid="invite-user-btn"
            >
              <UserPlus size={18} className="mr-2" />
              Convidar Usuário
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convidar Novo Usuário</DialogTitle>
            </DialogHeader>
            
            {!inviteLink ? (
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <Label>Nome Completo *</Label>
                  <Input
                    value={inviteForm.nome}
                    onChange={(e) => setInviteForm({ ...inviteForm, nome: e.target.value })}
                    placeholder="Nome do colaborador"
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="email@empresa.com"
                    required
                  />
                </div>
                <div>
                  <Label>Perfil de Acesso</Label>
                  <Select
                    value={inviteForm.role}
                    onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gestor">Gestor</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Gestores têm acesso às funcionalidades, Admins podem gerenciar usuários.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
                    <Mail size={16} className="mr-2" />
                    Enviar Convite
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-emerald-950 p-4 rounded-lg text-center">
                  <CheckCircle size={48} className="mx-auto text-emerald-500 mb-2" />
                  <p className="font-semibold text-emerald-700 dark:text-emerald-300">
                    Convite criado com sucesso!
                  </p>
                </div>
                
                <div>
                  <Label>Link do Convite</Label>
                  <div className="flex gap-2">
                    <Input value={inviteLink} readOnly className="text-xs" />
                    <Button type="button" variant="outline" onClick={copyInviteLink}>
                      <Copy size={16} />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Envie este link para {inviteForm.email}. O convite expira em 7 dias.
                  </p>
                </div>
                
                <DialogFooter>
                  <Button onClick={() => { setInviteOpen(false); resetInviteForm(); }}>
                    Fechar
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Usuários Ativos</p>
              <p className="text-2xl font-bold">{data.total_users} / {data.max_users}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg">
              <Clock size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Convites Pendentes</p>
              <p className="text-2xl font-bold">{data.pending_invites?.length || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${data.slots_available > 0 ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-red-100 dark:bg-red-900'}`}>
              <UserPlus size={24} className={data.slots_available > 0 ? 'text-emerald-600' : 'text-red-600'} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Vagas Disponíveis</p>
              <p className="text-2xl font-bold">{data.slots_available}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Limit Warning */}
      {data.slots_available <= 0 && (
        <Card className="p-4 bg-red-50 dark:bg-red-950 border-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle size={24} className="text-red-600" />
            <div>
              <p className="font-semibold text-red-900 dark:text-red-100">
                Limite de usuários atingido
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Seu plano permite até {data.max_users} usuários. Remova um usuário para adicionar novos.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Users List */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Usuários Ativos</h3>
        {data.users?.map((user) => (
          <Card key={user.id} className="p-4" data-testid={`user-${user.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-slate-100 dark:bg-slate-800 w-12 h-12 rounded-full flex items-center justify-center">
                  <span className="text-lg font-bold text-slate-600 dark:text-slate-300">
                    {user.nome?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 dark:text-white">{user.nome}</p>
                    {getRoleBadge(user.role)}
                  </div>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
              </div>
              {user.role !== 'admin' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => handleRemoveUser(user.id, user.nome)}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Pending Invites */}
      {data.pending_invites?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Convites Pendentes</h3>
          {data.pending_invites.map((invite) => (
            <Card key={invite.id} className="p-4 bg-yellow-50 dark:bg-yellow-950 border-yellow-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-yellow-100 dark:bg-yellow-900 w-12 h-12 rounded-full flex items-center justify-center">
                    <Mail size={20} className="text-yellow-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 dark:text-white">{invite.nome}</p>
                      {getRoleBadge(invite.role)}
                      <Badge className="bg-yellow-500">Pendente</Badge>
                    </div>
                    <p className="text-sm text-slate-500">{invite.email}</p>
                    <p className="text-xs text-slate-400">
                      Expira em: {new Date(invite.expires_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => handleCancelInvite(invite.id)}
                >
                  <XCircle size={16} className="mr-1" />
                  Cancelar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
